import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { ArrowLeft, FileText } from 'lucide-react'
import {
  SUBPROCESSORS,
  SECURITY_MEASURES,
  DATA_CATEGORIES,
  DATA_SUBJECTS,
  DELETION_BEHAVIOUR,
  RETENTION_DAYS,
  SUBPROCESSOR_NOTICE_DAYS,
  DPA_VERSION,
  DPA_EFFECTIVE_DATE,
  DPA_EFFECTIVE_DATE_LABEL,
  COMMON_PAPER_DPA_VERSION,
  COMMON_PAPER_DPA_URL,
  OPERATOR,
  LAST_UPDATED,
} from '../../content/trustFacts'

/**
 * Pre-signed Data Processing Agreement built on the Common Paper DPA Standard Terms,
 * as chosen in ROADMAP-RUNG2.md section 3.4: a filled cover page here, ten pages of
 * unmodified boilerplate hosted by Common Paper and incorporated by reference.
 *
 * Standard Terms version and cover-page field names verified 2026-09-09 against
 * https://commonpaper.com/standards/data-processing-agreement/ and
 * https://commonpaper.com/standards/data-processing-agreement/1.1
 *
 * Every fact on this page comes from app/src/content/trustFacts.ts so /dpa, /security
 * and /privacy cannot drift apart.
 */

const RETENTION_MONTHS = Math.round(RETENTION_DAYS / 30.44)

const VERSION_HISTORY = [
  {
    version: `v${DPA_VERSION}`,
    date: DPA_EFFECTIVE_DATE,
    note: 'First version. Cover page on Common Paper DPA Standard Terms v' + COMMON_PAPER_DPA_VERSION + '.',
  },
]

function CoverRow({ field, children }: { field: string; children: ReactNode }) {
  return (
    <tr className="border-b border-navy/10 align-top">
      <th scope="row" className="py-3 pr-4 text-left font-display font-normal text-navy w-56 leading-relaxed">
        {field}
      </th>
      <td className="py-3 text-navy/70 leading-relaxed">{children}</td>
    </tr>
  )
}

export default function DataProcessingAgreement() {
  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <Link to="/" className="flex items-center gap-2 hover:text-stone/80 transition">
          <ArrowLeft size={18} />
          <span className="hl text-xl text-stone">Day One</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <FileText size={28} className="text-amber" />
          <h1 className="hl text-[clamp(1.6rem,4.4vw,2.6rem)]">Data Processing Agreement</h1>
        </div>
        <p className="mono-label text-navy/70 mb-1">
          Version {DPA_VERSION}, effective {DPA_EFFECTIVE_DATE_LABEL}. Already signed by us. No countersignature needed.
        </p>
        <p className="mono-label text-navy/70 mb-10">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-navy max-w-none space-y-8 text-navy/80 leading-relaxed">

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">The short version</h2>
            <div className="not-prose bg-navy/5 border border-navy/10 rounded-lg p-5 space-y-3 text-navy/80 leading-relaxed">
              <p>
                <strong className="text-navy">What we process.</strong> Names and email addresses of the
                people who sign in, everything typed during a workshop, the text of documents uploaded for
                review, reflection answers, generated artifacts, and session feedback.
              </p>
              <p>
                <strong className="text-navy">Where it goes.</strong> Stored in Google Firebase in the
                United States. Each workshop turn is sent to OpenRouter, which routes it to Modal, the
                inference host for our primary model. If that path fails or is too slow, the turn goes to
                Anthropic instead. No subprocessor trains on it.
              </p>
              <p>
                <strong className="text-navy">How long.</strong> A session is deleted {RETENTION_DAYS} days
                after it is created, automatically, by a scheduled job.
              </p>
              <p>
                <strong className="text-navy">How to delete it sooner.</strong> Any user deletes their own
                sessions and account from inside the app, immediately, without asking us. Email{' '}
                <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                  {OPERATOR.privacyEmail}
                </a>{' '}
                if you would rather we did it, and we complete it within 30 days.
              </p>
            </div>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">How this agreement is put together</h2>
            <p>
              This DPA has two halves. The cover page below carries the details specific to Day One, and
              it incorporates by reference the{' '}
              <a href={COMMON_PAPER_DPA_URL} target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline">
                Common Paper Data Processing Agreement Standard Terms, Version {COMMON_PAPER_DPA_VERSION}
              </a>, published and hosted by Common Paper and used unchanged. That version stays available at
              that URL, so the text you review today is the text that applies.
            </p>
            <p>
              Capitalized terms not defined here have the meaning given in the Standard Terms. The Standard
              Terms govern processor and subprocessor obligations, processing instructions, restricted
              transfers, security incident response, audit and reports, cooperation, deletion, liability,
              order of precedence, and term.
            </p>
            <p>
              This DPA supplements the{' '}
              <Link to="/terms" className="text-amber hover:text-amber-dark underline">Terms of Service</Link>{' '}
              and should be read alongside the{' '}
              <Link to="/privacy" className="text-amber hover:text-amber-dark underline">Privacy Policy</Link>{' '}
              and the{' '}
              <Link to="/security" className="text-amber hover:text-amber-dark underline">IT &amp; Security Guide</Link>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Cover Page</h2>
            <div className="not-prose mt-4 -mx-6 px-6 md:mx-0 md:px-0 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm border-collapse">
                <tbody>
                  <CoverRow field="Provider (Processor)">
                    {OPERATOR.legalName}, {OPERATOR.address}, operator of Day One.
                  </CoverRow>
                  <CoverRow field="Customer (Controller)">
                    The organization, or the individual acting on their own behalf, that uses Day One under
                    an account. Accepting the Terms of Service accepts this DPA on that organization's behalf.
                  </CoverRow>
                  <CoverRow field="Agreement">
                    The Day One{' '}
                    <Link to="/terms" className="text-amber hover:text-amber-dark underline">Terms of Service</Link>,
                    together with this DPA.
                  </CoverRow>
                  <CoverRow field="Standard Terms">
                    Common Paper DPA Standard Terms Version {COMMON_PAPER_DPA_VERSION}, incorporated by
                    reference and unmodified:{' '}
                    <a href={COMMON_PAPER_DPA_URL} target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline break-words">
                      {COMMON_PAPER_DPA_URL}
                    </a>
                  </CoverRow>
                  <CoverRow field="Effective date">
                    {DPA_EFFECTIVE_DATE_LABEL}, or the date the Customer first used Day One, whichever is later.
                  </CoverRow>
                  <CoverRow field="Approved Subprocessors">
                    The list in the table below, which is the same list published on{' '}
                    <Link to="/security" className="text-amber hover:text-amber-dark underline">/security</Link>.
                  </CoverRow>
                  <CoverRow field="Subprocessor notice">
                    We give at least {SUBPROCESSOR_NOTICE_DAYS} days' advance notice by email before a new
                    subprocessor starts processing customer data, which is longer than the 10 business days
                    the Standard Terms require. Objections follow the Standard Terms. To join that notice
                    list, email{' '}
                    <a href={`mailto:${OPERATOR.securityEmail}`} className="text-amber hover:text-amber-dark underline">
                      {OPERATOR.securityEmail}
                    </a>.
                  </CoverRow>
                  <CoverRow field="Provider Security Contact">
                    <a href={`mailto:${OPERATOR.securityEmail}`} className="text-amber hover:text-amber-dark underline">
                      {OPERATOR.securityEmail}
                    </a>
                  </CoverRow>
                  <CoverRow field="Security Policy">
                    The technical and organizational measures in Annex II below, published in full on{' '}
                    <Link to="/security" className="text-amber hover:text-amber-dark underline">/security</Link>.
                  </CoverRow>
                  <CoverRow field="Security incident notice">
                    Without undue delay after becoming aware, and no later than 72 hours.
                  </CoverRow>
                  <CoverRow field="Audit and reports">
                    As set out in Section 5 of the Standard Terms. We hold no SOC 2 or ISO 27001 report
                    today. We answer security questionnaires and provide the documentation described on{' '}
                    <Link to="/security" className="text-amber hover:text-amber-dark underline">/security</Link>.
                  </CoverRow>
                  <CoverRow field="Changes to the Agreement">
                    We may update this DPA. A new version gets a new version number and effective date, and
                    the version history at the bottom of this page records the change. Material changes are
                    announced by notice on the site.
                  </CoverRow>
                  <CoverRow field="DPA Covered Claim and DPA Liability Cap">
                    As set out in the Limitation of Liability section of the{' '}
                    <Link to="/terms" className="text-amber hover:text-amber-dark underline">Terms of Service</Link>.
                    Day One is provided free of charge, so that cap is currently zero. A customer that needs a
                    fixed monetary cap can request a one-page order form at{' '}
                    <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                      {OPERATOR.privacyEmail}
                    </a>.
                  </CoverRow>
                  <CoverRow field="Governing Law and Chosen Courts">
                    The laws of the State of Colorado, with disputes resolved in the courts of the City and
                    County of Denver, Colorado, matching the Terms of Service.
                  </CoverRow>
                  <CoverRow field="Service Provider Relationship">
                    Yes. We act as a service provider under the CCPA and equivalent US state privacy laws. We
                    do not sell or share personal data, and we process it only to deliver Day One.
                  </CoverRow>
                  <CoverRow field="Restricted Transfers">
                    None. All processing described in this DPA takes place in the United States. Day One is
                    not currently offered with EU or UK data residency.
                  </CoverRow>
                  <CoverRow field="Governing Member State">
                    Not applicable, since there are no restricted transfers under this DPA.
                  </CoverRow>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Annex I(A). List of Parties</h2>
            <p>
              <strong>Data exporter (Controller):</strong> the Customer identified above, acting through the
              account that created the session. Contact: the account email address on file. Activities: uses
              Day One to run onboarding workshops led by AI agents. Role: Controller.
            </p>
            <p>
              <strong>Data importer (Processor):</strong> {OPERATOR.legalName}, {OPERATOR.address}. Contact:{' '}
              <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                {OPERATOR.privacyEmail}
              </a>. Activities: operates Day One and processes customer personal data to deliver it.
              Role: Processor.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Annex I(B). Description of Processing</h2>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Subject matter and nature of the processing</h3>
            <p>
              Hosting and delivery of a team onboarding workshop led by AI agents: storing session content,
              transmitting each turn to an AI provider to generate a facilitator reply, extracting text from
              uploaded documents for review, generating session artifacts, and sending a post-session
              summary by email.
            </p>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Purpose</h3>
            <p>
              To provide Day One to the Customer and no other purpose. We do not use customer personal data
              for advertising, profiling, resale, or model training, and no subprocessor receives it for
              those purposes.
            </p>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Categories of data subjects</h3>
            <ul className="list-disc pl-5 space-y-2">
              {DATA_SUBJECTS.map(subject => (
                <li key={subject}>{subject}</li>
              ))}
            </ul>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Categories of personal data</h3>
            <ul className="list-disc pl-5 space-y-2">
              {DATA_CATEGORIES.map(category => (
                <li key={category.label}>
                  <strong>{category.label}:</strong> {category.detail}
                </li>
              ))}
            </ul>
            <p>
              Day One does not ask for special categories of personal data and is not designed to hold them.
              Because a workshop is free text and participants can upload documents, the Customer is
              responsible for keeping sensitive data out of sessions.
            </p>

            <h3 className="font-display text-lg font-semibold text-navy mt-6 mb-2">Frequency and duration</h3>
            <p>
              Continuous for the duration of the Agreement, whenever a workshop is in progress. Retention is
              described below and in the{' '}
              <Link to="/privacy" className="text-amber hover:text-amber-dark underline">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Annex I(C). Competent Supervisory Authority</h2>
            <p>
              Not applicable. There are no restricted transfers under this DPA, so no EEA or UK supervisory
              authority is designated.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Approved Subprocessors</h2>
            <p>
              These are every third party that processes customer personal data on our behalf, what each one
              receives, and what each one says it keeps. We do not sell data, and no subprocessor receives it
              for advertising or model training.
            </p>
            <div className="not-prose mt-4 -mx-6 px-6 md:mx-0 md:px-0 overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-navy text-left">
                    <th className="font-display font-normal text-navy py-2 pr-4">Subprocessor</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">Role</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">Data it processes</th>
                    <th className="font-display font-normal text-navy py-2 pr-4">What it retains</th>
                    <th className="font-display font-normal text-navy py-2">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {SUBPROCESSORS.map(row => (
                    <tr key={row.name} className="border-b border-navy/10 align-top">
                      <td className="py-3 pr-4 leading-relaxed">
                        <a href={row.policyUrl} target="_blank" rel="noopener noreferrer" className="text-navy font-semibold underline decoration-navy/20 hover:decoration-navy">
                          {row.name}
                        </a>
                      </td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.role}</td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.data}</td>
                      <td className="py-3 pr-4 text-navy/70 leading-relaxed">{row.retention}</td>
                      <td className="py-3 text-navy/70 leading-relaxed">{row.location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm">
              We notify customers by email at least {SUBPROCESSOR_NOTICE_DAYS} days before a new
              subprocessor starts processing customer data. To be added to that list, email{' '}
              <a href={`mailto:${OPERATOR.securityEmail}`} className="text-amber hover:text-amber-dark underline">
                {OPERATOR.securityEmail}
              </a>. The same table is published on{' '}
              <Link to="/security" className="text-amber hover:text-amber-dark underline">/security</Link> and
              is updated in one place, so the two cannot disagree.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Annex II. Technical and Organizational Security Measures</h2>
            <ul className="list-disc pl-5 space-y-2">
              {SECURITY_MEASURES.map(measure => (
                <li key={measure}>{measure}</li>
              ))}
            </ul>
            <p>
              We hold no SOC 2 Type I or II report, no ISO 27001 certificate, and no third-party penetration
              test. We would rather say so here than have a reviewer discover it later.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Deletion and Return of Customer Personal Data</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Automatic deletion.</strong> Every session is deleted {RETENTION_DAYS} days (about{' '}
                {RETENTION_MONTHS} months) after it is created, together with its messages, participants,
                artifacts, reflections and feedback. A scheduled job runs daily and enforces it.
              </li>
              <li>
                <strong>Deletion on request.</strong> Users delete their own sessions and account from inside
                the app at any time. A request emailed to{' '}
                <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                  {OPERATOR.privacyEmail}
                </a>{' '}
                is completed within 30 days.
              </li>
              <li>
                <strong>On termination.</strong> Customer personal data is deleted on request per Section 7
                of the Standard Terms, and in any case falls out of the automatic window above.
              </li>
              <li>
                <strong>Export.</strong> Session data, artifacts and chat history can be downloaded from the
                app.
              </li>
            </ul>
            <p>What a deletion actually does:</p>
            <ul className="list-disc pl-5 space-y-2">
              {DELETION_BEHAVIOUR.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="text-sm">
              Two carve-outs, stated plainly. Where point-in-time recovery is enabled on our database, a
              deleted record can persist in recovery data until that window elapses; recovery data is never
              used to serve the product. And payment records, if the service ever charges, are kept for tax
              and accounting purposes with the account identifier removed.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Acceptance</h2>
            <div className="not-prose bg-navy/5 border border-navy/10 rounded-lg p-5 space-y-3 text-navy/80 leading-relaxed">
              <p>
                <strong className="text-navy">This DPA is already executed by the Processor.</strong>{' '}
                {OPERATOR.legalName} has signed version {DPA_VERSION} as of {DPA_EFFECTIVE_DATE_LABEL}. There
                is nothing for us to sign and nothing to wait on.
              </p>
              <p>
                A Customer accepts it in either of two ways. Using Day One under an account accepts it
                automatically, with no signature and no email to us. Or, if your process requires a document
                in the file, print or save this page, countersign it, and keep the copy for your records.
                Send a copy to{' '}
                <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                  {OPERATOR.privacyEmail}
                </a>{' '}
                if you want a signed copy back, and we will return one.
              </p>
              <div className="pt-2 border-t border-navy/10 text-sm">
                <p className="font-semibold text-navy mb-1">Processor</p>
                <p>{OPERATOR.legalName}</p>
                <p>{OPERATOR.address}</p>
                <p>Executed as of {DPA_EFFECTIVE_DATE_LABEL}, version {DPA_VERSION}</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Version History</h2>
            <ul className="list-none pl-0 space-y-3 mt-4">
              {VERSION_HISTORY.map(entry => (
                <li key={entry.version} className="border-l-2 border-amber/40 pl-4">
                  <span className="block text-sm font-semibold text-navy">{entry.version}, {entry.date}</span>
                  <span className="block text-sm text-navy/70">{entry.note}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm">
              Superseded versions stay reachable so procurement can retrieve the exact text they agreed to.
              Ask{' '}
              <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                {OPERATOR.privacyEmail}
              </a>{' '}
              for a copy of any earlier version.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Contact</h2>
            <p>
              Questions about this DPA, or a request for a countersigned copy:{' '}
              <a href={`mailto:${OPERATOR.privacyEmail}`} className="text-amber hover:text-amber-dark underline">
                {OPERATOR.privacyEmail}
              </a>. Security questions and subprocessor notices:{' '}
              <a href={`mailto:${OPERATOR.securityEmail}`} className="text-amber hover:text-amber-dark underline">
                {OPERATOR.securityEmail}
              </a>.
            </p>
            <p className="text-sm text-navy/70 mt-4">
              {OPERATOR.legalName}<br />
              Denver, CO
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}
